import { Dialog, DialogPortal } from "@/components/ui/dialog"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Button } from "@/components/ui/button"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Checkbox } from "@/components/ui/checkbox"
import { useState, useEffect } from "react"
import type { CookiePreferences } from "@/types/consent"
import { posthog } from "@/lib/posthog"

export function CookieConsentDialog({ isOpen, onOpenChange, onSavePreferences }: {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSavePreferences: (preferences: CookiePreferences) => void
}) {
  const [preferences, setPreferences] = useState<CookiePreferences>({
    analytics: true
  })

  useEffect(() => {
    setPreferences({
      analytics: !posthog.has_opted_out_capturing()
    })
  }, [])

  const handleSave = () => {
    onSavePreferences(preferences)
    onOpenChange(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className={cn(
          "fixed left-0 right-0 top-[50%] z-[60] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto",
          "sm:max-w-[400px]"
        )}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Cookie Preferences</h2>
              <DialogPrimitive.Close className="opacity-70 hover:opacity-100">
                <X className="h-5 w-5" />
              </DialogPrimitive.Close>
            </div>

            <div className="space-y-6">
              {/* Essential */}
              <div className="space-y-2">
                <div className="flex items-center">
                  <span className="font-medium">Essential Cookies</span>
                  <span className="ml-2 text-sm text-muted-foreground">(Required)</span>
                </div>
                <p className="text-sm text-muted-foreground">Required for the website to function.</p>
              </div>

              {/* Analytics */}
              <div className="space-y-2">
                <div className="flex items-center">
                  <Checkbox 
                    id="analytics"
                    checked={preferences.analytics}
                    onCheckedChange={(checked) => 
                      setPreferences({ analytics: checked as boolean })
                    }
                  />
                  <label htmlFor="analytics" className="ml-2 font-medium cursor-pointer">
                    Analytics
                  </label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Helps us improve the site by understanding how it's used.
                </p>
              </div>

              {/* Links */}
              <div className="flex gap-4 text-sm text-muted-foreground">
                <a href="/privacy-policy" className="hover:underline">Privacy Policy</a>
                <a href="/cookie-policy" className="hover:underline">Cookie Policy</a>
              </div>

              <div className="flex">
                <Button 
                  onClick={handleSave} 
                  className="w-full"
                >
                  {preferences.analytics ? 'Accept All' : 'Accept'}
                </Button>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
